import { useEffect, useState, useContext } from "react";
import axios from "axios";
import { getAppointments, deleteAppointment } from "../../api/bookingApi";
import { BarbershopContext } from "../../providers/useProvider";
import AlertMessage from "../../components/AlertMessage";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import { useAuthState } from "../../providers/AuthStateProvider";

const MyAppointments = () => {
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [message, setMessage] = useState(null);
  const [showMessage, setShowMessage] = useState(false);
  const [cancelAppointment, setCancelAppointment] = useState();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const { config } = useContext(BarbershopContext);
  const { user, logout } = useAuthState(); // Get user data from context

  useEffect(() => {
    fetchAppointments();
  }, []);
  const buildAppointmentDateTime = (dateStr, timeStr) => {
    const date = new Date(dateStr); // "2025-12-17T00:00:00"

    const [h, m, s = "0"] = timeStr.split(":");
    const seconds = parseFloat(s);

    date.setHours(
      Number(h),
      Number(m),
      Math.floor(seconds),
      Math.round((seconds % 1) * 1000),
    );

    return date;
  };
  const fetchAppointments = async () => {
    try {
      const response = await getAppointments(config.id);
      const now = new Date();

      const upcomingAppointments = response
        .filter(
          (appt) => buildAppointmentDateTime(appt.date, appt.startTime) >= now,
        )
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      const pastAppointments = response
        .filter(
          (appt) => buildAppointmentDateTime(appt.date, appt.startTime) < now,
        )
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      setUpcomingAppointments(upcomingAppointments);
      setPastAppointments(pastAppointments);
    } catch (error) {
      console.error("Failed to fetch appointment:", error);
    } finally {
      setLoading(false);
    }
  };
  const onCancelAppointment = (id) => {
    setCancelAppointment(id);
    setShowCancelModal(true);
  };

  const handleConfirmDelete = async () => {
    if (cancelAppointment) {
      try {
        var response = await deleteAppointment(cancelAppointment, config.id);
        if (response.data.isSuccess) {
          setMessage({ text: response.data.value, type: "success" });
          setShowMessage(true);
          setShowCancelModal(false);
          await fetchAppointments();
        } else {
          setMessage({
            text: response.data.errors.join("\n"),
            type: "danger",
          });
          setShowMessage(true);
          setShowCancelModal(false);
        }
      } catch (err) {
        setMessage({
          text: err,
          type: "danger",
        });
      }
    }
  };
  const onCloseCancelModal = () => setShowCancelModal(false);

  const cancelValidation = (date, time) => {
    const appointmentDateTime = buildAppointmentDateTime(date, time);
    const now = new Date();
    const nowPlus2Hours = new Date(
      now.getTime() + user.cancellationPeriod * 60 * 60 * 1000,
    );
    return appointmentDateTime >= nowPlus2Hours;
  };
  const renderAppointment = (app, showCancel) => (
    <div
      key={app.id}
      className="border mb-2 p-4 rounded-lg bg-gray-50 shadow-sm flex flex-col md:flex-row md:justify-between md:items-center"
    >
      <div className="space-y-1 text-gray-800 ">
        <p>
          <strong>Barber:</strong> {app.staffName}
        </p>
        <p>
          <strong>Date:</strong> {new Date(app.date).toLocaleDateString()}
        </p>
        <p>
          <strong>Time:</strong>{" "}
          {app.startTime.split(":").slice(0, 2).join(":")}
        </p>
        <p>
          <strong>Services:</strong>{" "}
          {app.services.map((s) => s.name).join(", ")}
        </p>
      </div>
      {showCancel && cancelValidation(app.date, app.startTime) && (
        <button
          onClick={() => onCancelAppointment(app.id)}
          className="mt-4 md:mt-0 bg-red-500 text-black px-4 py-2 rounded hover:bg-red-600"
        >
          Cancel
        </button>
      )}
    </div>
  );
  if (loading) {
    return (
      <div className="text-center mt-10 text-gray-600">
        Loading appointments...
      </div>
    );
  }

  return (
    <>
      <DeleteConfirmationModal
        show={showCancelModal}
        onClose={() => onCloseCancelModal()}
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to cancel this appointment?"
      />
      <div className="max-w-3xl mx-auto mt-10 p-5 bg-white shadow-lg rounded-xl">
        <h1 className="text-2xl font-bold mb-5">My Appointments</h1>
        {showMessage && (
          <AlertMessage
            key={message.text}
            setVisible={setShowMessage}
            message={message.text}
            type={message.type}
            duration={4000}
          />
        )}

        <div className="mb-3">
          <h3 className="text-xl font-semibold mb-4 text-blue-700">
            Upcoming Appointments
          </h3>
          {upcomingAppointments.length === 0 ? (
            <p className="text-gray-500">No upcoming appointments.</p>
          ) : (
            <div className="space-y-4">
              {upcomingAppointments.map((app) => renderAppointment(app, true))}
            </div>
          )}
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-4 text-gray-700">
            Past Appointments
          </h2>
          {pastAppointments.length === 0 ? (
            <p className="text-gray-500">No past appointments.</p>
          ) : (
            <div className="space-y-4">
              {pastAppointments.map((app) => renderAppointment(app, false))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MyAppointments;
